import React, { useState, useEffect } from 'react';
import type { Document, DocumentStatus } from '../types';
import { documentsAPI } from '../api';
import { 
  Eye, EyeOff, Printer, Download, CheckCircle, Clock, AlertCircle,
  FileText, Image as ImageIcon, File, FileSpreadsheet
} from 'lucide-react';

interface DocumentViewerProps {
  documents: Document[];
  onStatusUpdate: (documentId: string, status: DocumentStatus) => void;
  onBatchStatusUpdate?: (documentIds: string[], status: DocumentStatus) => void;
  onClose: () => void;
  senderName?: string;
  senderEmail?: string;
  hideHeader?: boolean;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ 
  documents, 
  onStatusUpdate, 
  onBatchStatusUpdate, 
  onClose, 
  senderName, 
  senderEmail,
  hideHeader = false
}) => {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [showBlurred, setShowBlurred] = useState<{ [key: string]: boolean }>({});
  const [printing, setPrinting] = useState<{ [key: string]: boolean }>({});
  const [documentUrls, setDocumentUrls] = useState<{ [key: string]: string }>({});
  const [modalDocument, setModalDocument] = useState<Document | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (modalDocument) {
          setModalDocument(null);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalDocument, onClose]);

  useEffect(() => {
    if (documents.length > 0 && !selectedDocument) {
      setSelectedDocument(documents[0]);
    }
  }, [documents, selectedDocument]);

  // PERFORMANCE OPTIMIZATION: Lazy load document only when selected
  useEffect(() => {
    const loadSelectedDocument = async () => {
      if (!selectedDocument || documentUrls[selectedDocument.id]) {
        return; // Already loaded
      }

      try {
        // Only download the currently selected document
        const blob = await documentsAPI.downloadFile(Number(selectedDocument.id));
        const blobUrl = URL.createObjectURL(blob);
        setDocumentUrls(prev => ({ ...prev, [selectedDocument.id]: blobUrl }));
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to load document');
        }
      }
    };

    if (selectedDocument && showBlurred[selectedDocument.id]) {
      // Only load when user wants to view the document
      loadSelectedDocument();
    }

    // Cleanup blob URLs on unmount
    return () => {
      Object.values(documentUrls).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [selectedDocument, showBlurred]);

  const handlePrint = async (document: Document) => {
    if (!document) return;

    setPrinting(prev => ({ ...prev, [document.id]: true }));

    // 1. Pre-open a blank window synchronously inside the click handler to bypass popup blockers
    const printWindow = window.open('about:blank', '_blank', 'width=800,height=600');
    if (!printWindow) {
      alert('Failed to open print window. Please allow popups for this website in your browser settings.');
      setPrinting(prev => ({ ...prev, [document.id]: false }));
      return;
    }

    // Write a loading message inside the pre-opened window while fetching the file
    printWindow.document.write('<html><head><title>Loading Document...</title><style>body{display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;color:#64748b;margin:0;}</style></head><body><div>Loading printing preview...</div></body></html>');

    try {
      // Get the document URL from cache or fetch it asynchronously
      let downloadUrl = documentUrls[document.id];
      if (!downloadUrl) {
        try {
          const blob = await documentsAPI.downloadFile(Number(document.id));
          downloadUrl = URL.createObjectURL(blob);
          setDocumentUrls(prev => ({ ...prev, [document.id]: downloadUrl }));
        } catch (err) {
          printWindow.close();
          throw err;
        }
      }

      // 2. Set the window's destination URL to the downloaded document
      printWindow.location.href = downloadUrl;

      // 3. Trigger printing on load
      const triggerPrint = () => {
        try {
          printWindow.print();
          // Mark as printed immediately after print dialog opens
          onStatusUpdate(String(document.id), 'printed');
          printWindow.addEventListener('afterprint', () => {
            printWindow.close();
          });
        } catch (e) {
          console.warn('Silent print trigger failed, user can print manually:', e);
        }
      };

      // Check if it's already loaded or wait for it
      if (printWindow.document.readyState === 'complete') {
        triggerPrint();
      } else {
        printWindow.addEventListener('load', triggerPrint);
      }
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('Print failed');
      }
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to print document';
      alert(`Failed to print document: ${errorMessage}`);
    } finally {
      setPrinting(prev => ({ ...prev, [document.id]: false }));
    }
  };

  const handleConfirmPrint = (document: Document) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Confirm Receipt',
      message: 'Confirm that the document has been successfully printed and received by the sender? This will instantly delete the document files.',
      onConfirm: () => {
        onStatusUpdate(String(document.id), 'confirmed');
      }
    });
  };

  const handleMarkAllPrinted = () => {
    const uploadedDocs = documents.filter(d => d.status === 'uploaded');
    if (uploadedDocs.length === 0) return;
    
    if (onBatchStatusUpdate) {
      setConfirmDialog({
        isOpen: true,
        title: 'Mark All Printed',
        message: `Are you sure you want to mark all ${uploadedDocs.length} documents as Printed?`,
        onConfirm: () => {
          onBatchStatusUpdate!(uploadedDocs.map(d => String(d.id)), 'printed');
        }
      });
    }
  };

  const toggleBlur = (documentId: string | number) => {
    setShowBlurred(prev => ({
      ...prev,
      [documentId]: !prev[documentId]
    }));
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'uploaded':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'printed':
        return <Printer className="w-4 h-4 text-orange-500" />;
      case 'confirmed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'deleted':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = (status: DocumentStatus) => {
    switch (status) {
      case 'uploaded':
        return 'Ready to Print';
      case 'printed':
        return 'Printed - Awaiting Confirmation';
      case 'confirmed':
        return 'Confirmed - Will be deleted soon';
      case 'deleted':
        return 'Deleted';
      default:
        return 'Unknown';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return <ImageIcon className="w-8 h-8 text-purple-500" />;
    if (contentType.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (contentType.includes('word') || contentType.includes('document')) return <FileText className="w-8 h-8 text-blue-500" />;
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
    return <File className="w-8 h-8 text-gray-500" />;
  };

  if (!selectedDocument) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <File className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No document selected</h3>
          <p className="text-gray-500">Select a document from the list to view details</p>
        </div>
      </div>
    );
  }
  
  const pendingCount = documents.filter(d => d.status === 'uploaded').length;

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: '#efeae2' }}>
      {/* Header - WhatsApp Web Style */}
      {!hideHeader && (
        <div className="border-b border-gray-200 px-4 py-2.5 flex-shrink-0 bg-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <button
                onClick={onClose}
                className="md:hidden p-2 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                aria-label="Back to sender list"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Sender avatar and info - WhatsApp style */}
              <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-gray-700 text-lg font-semibold">
                  {(senderName || senderEmail || documents[0]?.sender_name || documents[0]?.sender_email || 'S').charAt(0).toUpperCase()}
                </span>
              </div>

              <div className="min-w-0 flex-1">
                <h2 className="text-base font-medium text-gray-900 truncate">
                  {senderName || senderEmail || documents[0]?.sender_name || documents[0]?.sender_email || 'Sender'}
                </h2>
                <p className="text-xs text-gray-500 truncate">
                  {documents.length} {documents.length === 1 ? 'document' : 'documents'}
                </p>
              </div>
            </div>

            {/* Header action buttons - WhatsApp style */}
            <div className="flex items-center space-x-2">
              {pendingCount > 1 && onBatchStatusUpdate && (
                  <button
                      onClick={handleMarkAllPrinted}
                      className="hidden md:flex items-center space-x-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors text-xs font-medium mr-2"
                  >
                      <Printer className="w-4 h-4" />
                      <span>Mark {pendingCount} Printed</span>
                  </button>
              )}
              
              <button
                onClick={onClose}
                className="hidden md:block p-2 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Messages - Modern Clean Workspace Style */}
      <div className="flex-1 overflow-y-auto min-h-0 p-4 md:p-6 space-y-4 max-w-4xl w-full mx-auto">
        {documents.map((doc) => (
          <div key={doc.id} className="flex justify-start">
            {/* Document Card */}
            <div className="w-full max-w-xl">
              <div
                className="bg-white rounded-2xl shadow-sm border border-slate-200/90 overflow-hidden hover:shadow-md transition-all cursor-pointer"
                onClick={async () => {
                  setSelectedDocument(doc);
                  setModalDocument(doc);

                  // Load document if not already loaded
                  if (!documentUrls[doc.id]) {
                    try {
                      const blob = await documentsAPI.downloadFile(Number(doc.id));
                      const url = URL.createObjectURL(blob);
                      setDocumentUrls(prev => ({ ...prev, [doc.id]: url }));
                    } catch (error) {
                      console.error('Failed to load document:', error);
                    }
                  }
                }}
              >
                {/* Document header */}
                <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-white shadow-sm border border-slate-100 flex items-center justify-center flex-shrink-0">
                      {getFileIcon(doc.content_type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-black text-slate-900 truncate" title={doc.original_filename}>
                        {doc.original_filename}
                      </h4>
                      <p className="text-[11px] font-bold text-slate-400 flex items-center mt-0.5 uppercase tracking-tight">
                        {formatFileSize(doc.file_size)}
                        <span className="mx-1.5">•</span>
                        <span>{doc.content_type.split('/').pop()?.split('.').pop()}</span>
                        {doc.print_copies && doc.print_copies > 1 && (
                          <>
                            <span className="mx-1.5">•</span>
                            <span className="text-blue-600 font-black">{doc.print_copies} COPIES</span>
                          </>
                        )}
                        {doc.print_color && (
                          <>
                            <span className="mx-1.5">•</span>
                            <span className={doc.print_color === 'color' ? 'text-purple-600 font-black' : 'text-slate-600 font-black'}>
                              {doc.print_color === 'color' ? 'COLOR' : 'B&W'}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {/* Status indicator */}
                  <div className="flex-shrink-0 ml-3">
                    {doc.status === 'uploaded' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-100">
                        NEW
                      </span>
                    )}
                    {doc.status === 'printed' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100">
                        PRINTED
                      </span>
                    )}
                    {doc.status === 'confirmed' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                        DONE
                      </span>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="px-4 py-3 bg-white flex items-center justify-between gap-3">
                  {/* Left side - Download button */}
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        let url = documentUrls[doc.id];
                        if (!url) {
                          const blob = await documentsAPI.downloadFile(Number(doc.id));
                          url = URL.createObjectURL(blob);
                          setDocumentUrls(prev => ({ ...prev, [doc.id]: url }));
                        }
                        window.open(url, '_blank');
                      } catch (error) {
                        console.error('Failed to download document:', error);
                        alert('Failed to download document');
                      }
                    }}
                    className="flex items-center space-x-1.5 text-xs font-bold text-slate-600 hover:text-blue-600 transition-colors px-3 py-2 rounded-xl hover:bg-slate-100"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>

                  {/* Right side - Action buttons */}
                  <div className="flex items-center space-x-2">
                    {doc.status === 'uploaded' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrint(doc);
                        }}
                        disabled={printing[doc.id]}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all font-black text-xs uppercase tracking-wider shadow-md shadow-blue-500/20 active:scale-95"
                      >
                        <Printer className="w-4 h-4" />
                        <span>{printing[doc.id] ? 'Printing...' : 'Print'}</span>
                      </button>
                    )}

                    {doc.status === 'printed' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirmPrint(doc);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-black text-xs uppercase tracking-wider shadow-md shadow-emerald-500/20 active:scale-95"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Confirm Receipt</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Time stamp */}
                <div className="px-4 py-1.5 bg-slate-50/50 border-t border-slate-100 flex justify-between items-center text-[10px] font-medium text-slate-400">
                  <span>Click card to preview</span>
                  <span>
                    {new Date(doc.uploaded_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Document Modal Popup */}
      {modalDocument && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-[200] flex items-end sm:items-center justify-center sm:p-6 backdrop-blur-md"
          onClick={() => setModalDocument(null)}
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-3xl max-w-5xl w-full sm:h-[85vh] overflow-hidden flex flex-col shadow-2xl border border-slate-100"
            style={{ maxHeight: 'calc(100dvh - 64px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-white px-4 py-3 border-b border-slate-100 flex items-center gap-3 shrink-0">
              {/* File icon - smaller on mobile */}
              <div className="p-2 bg-slate-50 rounded-xl shrink-0">
                <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center">
                  {getFileIcon(modalDocument.content_type)}
                </div>
              </div>
              {/* Filename + meta — takes remaining space */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-black text-slate-800 truncate leading-snug" title={modalDocument.original_filename}>
                  {modalDocument.original_filename}
                </h3>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5 truncate">
                  {formatFileSize(modalDocument.file_size)}
                  <span className="mx-1.5 text-slate-300">•</span>
                  {modalDocument.content_type.split('/').pop()?.split('.').pop()}
                </p>
              </div>
              {/* Close button — always visible, fixed size */}
              <button
                onClick={() => setModalDocument(null)}
                className="w-9 h-9 shrink-0 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body - Document Preview (Flexible Height) */}
            <div className="flex-grow overflow-hidden p-3 md:p-5 bg-slate-50 flex items-center justify-center">
              {documentUrls[modalDocument.id] ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  {modalDocument.content_type.startsWith('image/') ? (
                    <img
                      src={documentUrls[modalDocument.id]}
                      alt={modalDocument.filename}
                      className="max-w-full max-h-full object-contain rounded-2xl shadow-lg border border-slate-100"
                    />
                  ) : modalDocument.content_type.includes('pdf') ? (
                    <iframe
                      src={documentUrls[modalDocument.id]}
                      className="w-full h-full rounded-2xl shadow-lg bg-white border border-slate-100"
                      title={modalDocument.filename}
                    />
                  ) : (
                    <div className="text-center p-8 md:p-12 bg-white rounded-3xl shadow-md max-w-sm border border-slate-100">
                      <div className="mb-5 flex justify-center">
                        {getFileIcon(modalDocument.content_type)}
                      </div>
                      <h3 className="text-lg font-black text-slate-800 mb-1.5">
                        Preview Unavailable
                      </h3>
                      <p className="text-xs text-slate-400 font-bold leading-relaxed mb-6">
                        This file format cannot be viewed directly. Please download the document to inspect it.
                      </p>
                      <button
                        onClick={() => window.open(documentUrls[modalDocument.id], '_blank')}
                        className="inline-flex items-center space-x-2 px-6 py-3.5 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-500/20 active:scale-95"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download Document</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-12 h-12 border-4 border-blue-600/35 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                  <p className="text-xs text-slate-400 font-black uppercase tracking-widest">Loading document...</p>
                </div>
              )}
            </div>

            {/* Modal Footer - Actions */}
            <div className="bg-white px-4 py-3 border-t border-slate-100 flex items-center gap-2.5 shrink-0">
              {/* Status label — hidden on mobile to save space */}
              <div className="hidden sm:flex items-center space-x-2 text-xs font-bold text-slate-400 shrink-0 mr-auto">
                {getStatusIcon(modalDocument.status)}
                <span className="uppercase tracking-wider">{getStatusText(modalDocument.status)}</span>
              </div>

              {/* Action buttons — always row, full-width on mobile */}
              <div className="flex items-center gap-2 flex-1 sm:flex-initial justify-end">
                <button
                  onClick={async () => {
                    try {
                      let url = documentUrls[modalDocument.id];
                      if (!url) {
                        const blob = await documentsAPI.downloadFile(Number(modalDocument.id));
                        url = URL.createObjectURL(blob);
                      }
                      window.open(url, '_blank');
                    } catch (error) {
                      console.error('Failed to download:', error);
                      alert('Failed to download document');
                    }
                  }}
                  className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-600 rounded-2xl hover:bg-slate-200 transition-colors font-bold text-xs uppercase tracking-wider"
                >
                  <Download className="w-4 h-4 shrink-0" />
                  <span>Download</span>
                </button>

                {modalDocument.status === 'uploaded' && (
                  <button
                    onClick={() => {
                      handlePrint(modalDocument);
                      setModalDocument(null);
                    }}
                    disabled={printing[modalDocument.id]}
                    className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 disabled:opacity-50 transition-all font-black text-xs uppercase tracking-wider shadow-lg shadow-blue-500/20 active:scale-95"
                  >
                    <Printer className="w-4 h-4 shrink-0" />
                    <span>{printing[modalDocument.id] ? 'Printing...' : 'Print Now'}</span>
                  </button>
                )}

                {modalDocument.status === 'printed' && (
                  <button
                    onClick={() => {
                      handleConfirmPrint(modalDocument);
                      setModalDocument(null);
                    }}
                    className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-2xl hover:bg-green-700 transition-all font-black text-xs uppercase tracking-wider shadow-lg shadow-green-500/20 active:scale-95"
                  >
                    <CheckCircle className="w-4 h-4 shrink-0" />
                    <span>Confirm</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirm Dialog Modal */}
      {confirmDialog && confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[300] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl border border-slate-50 animate-scaleIn">
            <h3 className="text-lg font-black text-slate-800 mb-2">{confirmDialog.title}</h3>
            <p className="text-xs text-slate-500 font-bold mb-6 leading-relaxed">{confirmDialog.message}</p>
            <div className="flex space-x-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="flex-grow py-3 px-4 bg-slate-50 text-slate-600 rounded-xl hover:bg-slate-100 transition-colors font-bold text-xs uppercase tracking-wider border border-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmDialog.onConfirm();
                  setConfirmDialog(null);
                }}
                className="flex-grow py-3 px-4 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-black text-xs uppercase tracking-widest shadow-lg shadow-green-500/20 active:scale-95"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DocumentViewer;